import { getCSRFToken } from '@leaftrade/leaftrade-component-library';

interface Config {
  heartbeatInterval?: number;
  idleTimeout?: number;
  verbose?: boolean;
}

class UserActivityTracker {
  private worker: SharedWorker | null = null;
  private port: MessagePort | null = null;
  private isInitialized: boolean = false;
  private lastActivitySent: number = 0;
  private verbose: boolean = false;

  private log(...args: unknown[]): void {
    if (this.verbose) {
      console.log(...args);
    }
  }

  public init(config?: Config): void {
    if (this.isInitialized) {
      return;
    }

    try {
      this.worker = new SharedWorker('/static/assets/worker.js');

      this.worker.onerror = (error: ErrorEvent): void => {
        console.error('SharedWorker error:', error);
      };

      this.port = this.worker.port;

      this.port.onmessageerror = (error: MessageEvent): void => {
        console.error('Port message error:', error);
      };

      this.port.onmessage = (event: MessageEvent): void => {
        this.log('Message from worker:', event.data);
      };

      this.port.start();

      if (config) {
        this.configure(config);
      }

      this.setupActivityListeners();
      this.sendCSRFToken();

      this.isInitialized = true;
      this.log('User activity tracker initialized');
    } catch (error) {
      console.error('Failed to initialize user activity tracker:', error);
    }
  }

  private setupActivityListeners(): void {
    const activityEvents: string[] = [
      'mousedown',
      'mousemove',
      'keypress',
      'keydown',
      'scroll',
      'touchstart',
      'touchmove',
      'click',
      'focus',
      'blur',
    ];

    const throttleMs: number = 200;

    const handleActivity = (): void => {
      const now = Date.now();
      if (now - this.lastActivitySent > throttleMs) {
        this.notifyWorker();
        this.lastActivitySent = now;
      }
    };

    activityEvents.forEach((eventName: string): void => {
      document.addEventListener(eventName, handleActivity, { passive: true });
    });

    window.addEventListener('focus', handleActivity);
    window.addEventListener('blur', handleActivity);
    this.notifyWorker();
  }

  private notifyWorker(): void {
    if (this.port) {
      try {
        this.port.postMessage('user-activity');
      } catch (error) {
        console.error('Failed to notify worker of user activity:', error);
      }
    }
  }

  private sendCSRFToken(): void {
    const csrfToken: string | null = getCSRFToken();
    if (csrfToken && this.port) {
      this.port.postMessage({
        type: 'csrf-token',
        token: csrfToken,
      });
      this.log('CSRF token sent to worker');
    } else {
      console.warn('No CSRF token found');
    }
  }

  public configure(config: Config): void {
    if (this.port) {
      this.port.postMessage({
        type: 'config',
        heartbeatInterval: config.heartbeatInterval,
        idleTimeout: config.idleTimeout,
        verbose: config.verbose,
      });
      this.log('Configuration sent to worker:', config);

      if (config.verbose !== undefined) {
        this.verbose = config.verbose;
      }
    } else {
      console.warn('Cannot send config - worker not initialized');
    }
  }
}

const userActivityTracker = new UserActivityTracker();

export default userActivityTracker;
